import axios from 'axios';
import supabase, { isSupabaseAuthEnabled } from './supabase';
import { isNative } from './capacitor';
import { PUBLIC_HOST } from '../utils/hosts';
import {
  isSupabaseDataEnabled,
  supabaseBlockedSendersAPI,
  supabaseTagsAPI,
  supabaseTemplatesAPI,
  supabaseListsAPI,
  supabaseContactsAPI
} from './supabaseData';

// On Capacitor (iOS/Android) the page origin is capacitor://localhost, so
// a relative '/api' resolves to a non-existent URL. Force the public host
// so login/network calls actually reach the backend.
const API_URL = process.env.REACT_APP_API_URL
  || (isNative ? `${PUBLIC_HOST}/api` : '/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

// Subscribe to token refresh
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

// Notify all subscribers when token is refreshed
function onTokenRefreshed(accessToken) {
  refreshSubscribers.forEach(callback => callback(accessToken));
  refreshSubscribers = [];
}

// Add auth token to requests
api.interceptors.request.use(
  async (config) => {
    let accessToken = null;

    // If Supabase auth is enabled and NOT masquerading, get fresh token from Supabase session
    if (isSupabaseAuthEnabled() && localStorage.getItem('isMasquerading') !== 'true') {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          accessToken = session.access_token;
          // Keep localStorage in sync for components that read it directly
          localStorage.setItem('accessToken', accessToken);
        }
      } catch (e) {
        // Fall through to localStorage
      }
    }

    // Fall back to localStorage token (legacy auth or Supabase sync)
    if (!accessToken) {
      accessToken = localStorage.getItem('accessToken');
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // If the request data is FormData, let the browser set Content-Type automatically
    // This is important for multipart/form-data uploads with proper boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors and automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Phantom / orphaned account: the Supabase session validates
      // (auth.users exists) but our public.users row was deleted or
      // deactivated. Refreshing would succeed and 401 again — kill
      // the Supabase session and bounce to login. Header takes
      // precedence over the JSON code so a mis-serialized error body
      // still triggers the sign-out.
      const orphaned = error.response?.headers?.['x-auth-reason'] === 'orphaned-account'
        || error.response?.data?.code === 'ORPHANED_ACCOUNT';
      if (orphaned) {
        try { await supabase.auth.signOut(); } catch {}
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.replace('/login?session_expired=orphaned');
        }
        return Promise.reject(error);
      }
      // If using Supabase auth, let Supabase handle refresh
      if (isSupabaseAuthEnabled()) {
        try {
          const { data: { session }, error: refreshErr } = await supabase.auth.refreshSession();
          if (refreshErr || !session) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login') {
              window.location.replace('/login?session_expired=true');
            }
            return Promise.reject(error);
          }
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          localStorage.setItem('accessToken', session.access_token);
          return axios(originalRequest);
        } catch (e) {
          return Promise.reject(error);
        }
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve) => {
          subscribeTokenRefresh((accessToken) => {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            resolve(axios(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Try to refresh the token
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken
        });

        const { accessToken } = response.data;

        // Update stored access token
        localStorage.setItem('accessToken', accessToken);

        // Update the failed request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Notify all queued requests
        onTokenRefreshed(accessToken);
        isRefreshing = false;

        // Retry the original request
        return axios(originalRequest);
      } catch (refreshError) {
        // Refresh failed, log out user
        console.error('Token refresh failed, logging out:', refreshError.message);
        isRefreshing = false;
        refreshSubscribers = [];

        // Clear all auth data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');

        // Force redirect to login
        if (window.location.pathname !== '/login') {
          window.location.replace('/login?session_expired=true');
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  updateProfile: (data) => api.put('/auth/profile', data),
  masquerade: (userId) => api.post(`/auth/masquerade/${userId}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verify2FA: (userId, code, trustDevice = false) => api.post('/auth/verify-2fa', { userId, code, trustDevice }),
  enable2FA: () => api.post('/auth/enable-2fa'),
  disable2FA: () => api.post('/auth/disable-2fa'),
  get2FAStatus: () => api.get('/auth/2fa-status'),
  getTrustedDevices: () => api.get('/auth/trusted-devices'),
  revokeTrustedDevice: (id) => api.delete(`/auth/trusted-devices/${id}`),
  revokeAllTrustedDevices: () => api.delete('/auth/trusted-devices'),
  getUserStatus: () => api.get('/user-status/me'),
  updateUserStatus: (status) => api.put('/user-status/me/status', { status }),
  updateAutoReply: (data) => api.put('/user-status/me/auto-reply', data),
  getStatusSchedule: () => api.get('/user-status/me/schedule'),
  updateStatusSchedule: (data) => api.put('/user-status/me/schedule', data)
};

// Messages API
export const messagesAPI = {
  getAll: (params) => api.get('/messages', { params }),
  getById: (id) => api.get(`/messages/${id}`),
  send: (data) => api.post('/messages/send', data),
  retry: (id) => api.post(`/messages/${id}/retry`),
  getStats: (days) => api.get('/messages/stats/summary', { params: { days } }),
  getUnreadCount: () => api.get('/messages/unread/count')
};

// Contacts API
export const contactsAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  copy: (id, { company, user_id }) => api.post(`/contacts/${id}/copy`, { company, user_id }),
  bulkCopyToCompany: (payload) => api.post('/contacts/bulk-copy-to-company', payload),
  // Import/Export
  importPreview: (formData) => api.post('/contacts/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importProcess: (formData) => api.post('/contacts/import/process', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getImportStatus: (id) => api.get(`/contacts/import/${id}/status`),
  getImportHistory: (params) => api.get('/contacts/import/history', { params }),
  rollbackImport: (id) => api.post(`/contacts/import/${id}/rollback`),
  exportContacts: (data) => api.post('/contacts/export', data, { responseType: 'blob' }),
  getExportHistory: (params) => api.get('/contacts/export/history', { params })
};

// Templates API
export const templatesAPI = {
  getAll: (params) => api.get('/templates', { params }),
  getById: (id) => api.get(`/templates/${id}`),
  getCategories: () => api.get('/templates/categories'),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  incrementUsage: (id) => api.post(`/templates/${id}/use`),
  uploadMedia: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/templates/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// Tags API
export const tagsAPI = {
  getAll: () => api.get('/tags'),
  getById: (id) => api.get(`/tags/${id}`),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  addToConversation: (tagId, conversationId) => api.post(`/tags/${tagId}/conversations/${conversationId}`),
  removeFromConversation: (tagId, conversationId) => api.delete(`/tags/${tagId}/conversations/${conversationId}`),
  getConversationTags: (conversationId) => api.get(`/tags/conversations/${conversationId}`)
};

// Lists API (Allow/Opt-Out/Blast Lists)
export const listsAPI = {
  getOptOutList: (params) => api.get('/lists/opt-out', { params }),
  addToOptOutList: (data) => api.post('/lists/opt-out', data),
  removeFromOptOutList: (id) => api.delete(`/lists/opt-out/${id}`),
  removeFromOptOutListByPhone: (phone) => api.delete(`/lists/opt-out/phone/${phone}`),
  getAllowList: (params) => api.get('/lists/allow', { params }),
  addToAllowList: (data) => api.post('/lists/allow', data),
  removeFromAllowList: (id) => api.delete(`/lists/allow/${id}`),
  removeFromAllowListByPhone: (phone) => api.delete(`/lists/allow/phone/${phone}`),
  checkPhone: (phone) => api.get(`/lists/check/${phone}`),
  // Blast Lists
  getBlastLists: () => api.get('/lists/blast-lists'),
  getBlastListById: (id) => api.get(`/lists/blast-lists/${id}`),
  createBlastList: (data) => api.post('/lists/blast-lists', data),
  updateBlastList: (id, data) => api.put(`/lists/blast-lists/${id}`, data),
  deleteBlastList: (id) => api.delete(`/lists/blast-lists/${id}`),
  addMembersToBlastList: (id, contact_ids) => api.post(`/lists/blast-lists/${id}/members`, { contact_ids }),
  removeMemberFromBlastList: (listId, contactId) => api.delete(`/lists/blast-lists/${listId}/members/${contactId}`)
};

// Blocked Senders API
export const blockedSendersAPI = {
  getAll: (params) => api.get('/blocked-senders', { params }),
  block: (data) => api.post('/blocked-senders', data),
  unblock: (id) => api.delete(`/blocked-senders/${id}`),
  checkPhone: (phone) => api.get(`/blocked-senders/check/${phone}`)
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  updateSetting: (key, value) => api.put(`/settings/${key}`, { value }),
  deleteSetting: (key) => api.delete(`/settings/${key}`),
  testBandwidth: () => api.post('/settings/test-bandwidth'),
  testEmail: (email) => api.post('/settings/test-email', { email }),
  testAnthropic: () => api.post('/settings/test-ai'),
  testAI: () => api.post('/settings/test-ai'),
  getAIFeatures: () => api.get('/settings/ai-features'),
  updateAIFeatures: (enabled) => api.put('/settings/ai-features', { enabled }),
  getAIProvider: () => api.get('/settings/ai-provider'),
  updateAIProvider: (config) => api.put('/settings/ai-provider', config),
  getGroupMessaging: () => api.get('/settings/group-messaging'),
  getTextBlasts: () => api.get('/settings/text-blasts'),
  getIntegrations: () => api.get('/settings/integrations'),
  getInternational: () => api.get('/settings/international'),
  getMessageFooter: () => api.get('/settings/message-footer'),
  getSendUsage: () => api.get('/settings/send-usage'),
  updateGroupMessaging: (enabled) => api.put('/settings/group-messaging', { enabled }),
  getBranding: () => api.get('/settings/branding'),
  getBrandingAdmin: () => api.get('/settings/branding/admin'),
  updateBranding: (data) => api.put('/settings/branding', data),
  uploadBrandingImage: (file, type) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);
    return api.post('/settings/branding/upload', formData);
  },
  deleteBrandingImage: (type) => api.delete(`/settings/branding/image/${type}`)
};

// Admin - Tenants API
export const tenantsAPI = {
  getAll: () => api.get('/admin/tenants'),
  getById: (id) => api.get(`/admin/tenants/${id}`),
  create: (data) => api.post('/admin/tenants', data),
  update: (id, data) => api.put(`/admin/tenants/${id}`, data),
  delete: (id) => api.delete(`/admin/tenants/${id}`),
  toggleAIFeatures: (id, enabled) => api.put(`/admin/tenants/${id}/ai-features`, { enabled }),
  toggleGroupMessaging: (id, enabled) => api.put(`/admin/tenants/${id}/group-messaging`, { enabled }),
  toggleTextBlasts: (id, enabled) => api.put(`/admin/tenants/${id}/text-blasts`, { enabled }),
  toggleIntegrations: (id, enabled) => api.put(`/admin/tenants/${id}/integrations`, { enabled }),
  toggleRcs: (id, enabled) => api.put(`/admin/tenants/${id}/rcs`, { enabled }),
  setRcsSender: (id, senderId) => api.put(`/admin/tenants/${id}/rcs-sender`, { sender_id: senderId }),
  toggleInternational: (id, enabled, daily_limit) => api.put(`/admin/tenants/${id}/international`, { enabled, daily_limit }),
  updateMessageFooter: (id, { enabled, text }) => api.put(`/admin/tenants/${id}/message-footer`, { enabled, text }),
  getSendLimits: (id) => api.get(`/admin/tenants/${id}/send-limits`),
  updateSendLimits: (id, data) => api.put(`/admin/tenants/${id}/send-limits`, data),
  getSendUsage: (id) => api.get(`/admin/tenants/${id}/send-usage`),
  // AI Chatbot Beta — super_admin only during beta
  enableAiChatbot: (id) => api.post(`/admin/tenants/${id}/ai-chatbot/enable`),
  disableAiChatbot: (id) => api.post(`/admin/tenants/${id}/ai-chatbot/disable`),
  getAiChatbotConfig: (id) => api.get(`/admin/tenants/${id}/ai-chatbot/config`),
  updateAiChatbotConfig: (id, data) => api.put(`/admin/tenants/${id}/ai-chatbot/config`, data),
  setAiChatbotKillSwitch: (id, active) => api.post(`/admin/tenants/${id}/ai-chatbot/kill-switch`, { active }),
  getAiChatbotActivity: (id, limit = 25) => api.get(`/admin/tenants/${id}/ai-chatbot/activity`, { params: { limit } }),
  getAiChatbotPreviewSamples: (id, limit = 5) => api.get(`/admin/tenants/${id}/ai-chatbot/preview/samples`, { params: { limit } }),
  runAiChatbotPreview: (id, text) => api.post(`/admin/tenants/${id}/ai-chatbot/preview`, { text }),
};

// Admin - Users API
export const adminUsersAPI = {
  getAll: (params) => api.get('/admin/users', { params }),
  getById: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
  sendResetEmail: (id) => api.post(`/admin/users/${id}/send-reset-email`),
  getLockedAccounts: () => api.get('/admin/users/locked/list'),
  unlockAccount: (id) => api.post(`/admin/users/${id}/unlock`),
  getLoginAttempts: (id, limit) => api.get(`/admin/users/${id}/login-attempts`, { params: { limit } }),
  enable2FA: (id) => api.post(`/admin/users/${id}/enable-2fa`),
  disable2FA: (id) => api.post(`/admin/users/${id}/disable-2fa`)
};

// Text Numbers API
export const phoneNumbersAPI = {
  getAll: (opts = {}) => api.get('/phone-numbers', { params: opts }),
  getByTenant: (tenantId) => api.get(`/phone-numbers/by-tenant/${tenantId}`),
  getById: (id) => api.get(`/phone-numbers/${id}`),
  create: (data) => api.post('/phone-numbers', data),
  update: (id, data) => api.put(`/phone-numbers/${id}`, data),
  delete: (id) => api.delete(`/phone-numbers/${id}`),
  assignUser: (id, userId) => api.post(`/phone-numbers/${id}/assign`, { userId }),
  unassignUser: (id, userId) => api.delete(`/phone-numbers/${id}/assign/${userId}`),
  getCampaignInfo: (id) => api.get(`/phone-numbers/${id}/campaign`),
  getBwStatus: (id) => api.get(`/phone-numbers/${id}/bw-status`),
  activate: (id) => api.post(`/phone-numbers/${id}/activate`),
  reassignTenant: (id, tenantId) => api.post(`/phone-numbers/${id}/reassign-tenant`, { tenantId }),
  saveRouting: (id, config) => api.put(`/phone-numbers/${id}/routing`, config)
};

// Conversations API
export const conversationsAPI = {
  getAll: (params) => api.get('/conversations', { params }),
  getById: (id) => api.get(`/conversations/${id}`),
  getArchivedMessages: (id) => api.get(`/conversations/${id}/archived-messages`),
  transfer: (id, toUserId, notes) => api.post(`/conversations/${id}/transfer`, { toUserId, notes }),
  claim: (id) => api.post(`/conversations/${id}/claim`),
  getTransfers: (id) => api.get(`/conversations/${id}/transfers`),
  getOrCreate: (phoneNumberId, contactPhoneNumber) =>
    api.post('/conversations/get-or-create', { phoneNumberId, contactPhoneNumber }),
  archive: (id) => api.post(`/conversations/${id}/archive`),
  unarchive: (id) => api.post(`/conversations/${id}/unarchive`),
  delete: (id) => api.delete(`/conversations/${id}`),
  markRead: (id) => api.post(`/conversations/${id}/mark-read`),
  markUnread: (id) => api.post(`/conversations/${id}/mark-unread`)
};

// Translation API (LibreTranslate)
export const translationAPI = {
  status: () => api.get('/translation/status'),
  languages: () => api.get('/translation/languages'),
  detect: (text) => api.post('/translation/detect', { text }),
  translate: (text, target, source) => api.post('/translation/translate', { text, target, source }),
};

// Conversation Assignments API
export const assignmentsAPI = {
  transfer: (conversationId, toUserId, notes, removeOldOwner = true) =>
    api.post('/conversation-assignments/transfer', { conversationId, toUserId, notes, removeOldOwner }),
  share: (conversationId, withUserId, assignmentType = 'collaborator') =>
    api.post('/conversation-assignments/share', { conversationId, withUserId, assignmentType }),
  claim: (conversationId) =>
    api.post('/conversation-assignments/claim', { conversationId }),
  remove: (conversationId, userId) =>
    api.delete(`/conversation-assignments/${conversationId}/${userId}`),
  getHistory: (conversationId) =>
    api.get(`/conversation-assignments/history/${conversationId}`),
  getAssignments: (conversationId) =>
    api.get(`/conversation-assignments/${conversationId}`),
  setVisibility: (conversationId, visibility_mode) =>
    api.put('/conversation-assignments/visibility', { conversationId, visibility_mode }),
  getSettings: () =>
    api.get('/conversation-assignments/settings/tenant'),
  updateSettings: (settings) =>
    api.put('/conversation-assignments/settings/tenant', { settings }),
  getUsers: () =>
    api.get('/conversation-assignments/users')
};

// Notes API
export const notesAPI = {
  getByConversation: (conversationId) => api.get(`/notes/conversation/${conversationId}`),
  getImportant: () => api.get('/notes/important'),
  search: (query) => api.get('/notes/search', { params: { query } }),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
  getMentions: () => api.get('/notes/mentions'),
  markMentionRead: (id) => api.post(`/notes/mentions/${id}/read`)
};

// Auto-responders API
export const autoRespondersAPI = {
  getAll: () => api.get('/auto-responders'),
  getById: (id) => api.get(`/auto-responders/${id}`),
  create: (data) => api.post('/auto-responders', data),
  update: (id, data) => api.put(`/auto-responders/${id}`, data),
  delete: (id) => api.delete(`/auto-responders/${id}`),
  getLogs: (id, params) => api.get(`/auto-responders/${id}/logs`, { params })
};

// Activity Logs API (Super Admin)
export const logsAPI = {
  getAll: (params) => api.get('/admin/logs', { params }),
  getStats: () => api.get('/admin/logs/stats'),
  getSecurity: (params) => api.get('/admin/logs/security', { params }),
  getErrors: (params) => api.get('/admin/logs/errors', { params })
};

// Billing API
export const billingAPI = {
  getSummary: (params) => api.get('/billing/summary', { params }),
  exportData: (params) => api.get('/billing/export', { params, responseType: 'blob' }),
  getChartData: (params) => api.get('/billing/chart-data', { params }),
  getAllTenants: (params) => api.get('/billing/all-tenants', { params }),
  getTenantUsers: (tenantId, params) => api.get(`/billing/tenant/${tenantId}/users`, { params }),
  getAITokenUsage: (days = 30) => api.get(`/billing/ai-token-usage?days=${days}`)
};

// Text Blasts API
export const textBlastsAPI = {
  getAll: () => api.get('/text-blasts'),
  getById: (id) => api.get(`/text-blasts/${id}`),
  create: (data) => api.post('/text-blasts', data),
  send: (id) => api.post(`/text-blasts/${id}/send`),
  cancel: (id) => api.post(`/text-blasts/${id}/cancel`),
  delete: (id) => api.delete(`/text-blasts/${id}`),
  getOptInStats: () => api.get('/text-blasts/stats/opt-in')
};

// Scheduled Messages API
export const scheduledMessagesAPI = {
  getAll: (params) => api.get('/scheduled-messages', { params }),
  getById: (id) => api.get(`/scheduled-messages/${id}`),
  create: (data) => api.post('/scheduled-messages', data),
  update: (id, data) => api.put(`/scheduled-messages/${id}`, data),
  cancel: (id) => api.delete(`/scheduled-messages/${id}`)
};

// Basic Assignments API (legacy)
export const basicAssignmentsAPI = {
  getUnassigned: () => api.get('/assignments/unassigned'),
  assign: (conversationId, userId) => api.post(`/assignments/${conversationId}/assign`, { userId }),
  autoAssign: (conversationId) => api.post(`/assignments/${conversationId}/auto-assign`),
  getRules: () => api.get('/assignments/rules'),
  createRule: (data) => api.post('/assignments/rules', data),
  updateRule: (id, data) => api.put(`/assignments/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/assignments/rules/${id}`)
};

// Backups API
export const backupsAPI = {
  getAll: () => api.get('/backups'),
  create: (data) => api.post('/backups/create', data),
  download: (id) => api.get(`/backups/download/${id}`),
  delete: (id) => api.delete(`/backups/${id}`),
  restore: (id) => api.post(`/backups/restore/${id}`),
  testS3: () => api.post('/backups/test-s3')
};

// API Keys API
export const apiKeysAPI = {
  getAll: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  update: (id, data) => api.put(`/api-keys/${id}`, data),
  delete: (id) => api.delete(`/api-keys/${id}`),
  getUsage: (id, days) => api.get(`/api-keys/${id}/usage`, { params: { days } })
};

// Analytics API
export const analyticsAPI = {
  getResponseTimes: (days) => api.get('/analytics/response-times', { params: { days } }),
  getResponseTimesByUser: (days) => api.get('/analytics/response-times/by-user', { params: { days } }),
  getSLASettings: () => api.get('/analytics/sla-settings'),
  updateSLASettings: (data) => api.put('/analytics/sla-settings', data),
  getSlowResponses: (limit) => api.get('/analytics/conversations/slow-responses', { params: { limit } })
};

// Drip Campaigns API
export const dripCampaignsAPI = {
  getAll: (params) => api.get('/drip-campaigns', { params }),
  getById: (id) => api.get(`/drip-campaigns/${id}`),
  create: (data) => api.post('/drip-campaigns', data),
  update: (id, data) => api.put(`/drip-campaigns/${id}`, data),
  delete: (id) => api.delete(`/drip-campaigns/${id}`),
  enroll: (id, contact_ids) => api.post(`/drip-campaigns/${id}/enroll`, { contact_ids }),
  getEnrollments: (id, params) => api.get(`/drip-campaigns/${id}/enrollments`, { params }),
  cancelEnrollment: (enrollmentId, reason) => api.post(`/drip-campaigns/enrollments/${enrollmentId}/cancel`, { reason }),
  pauseEnrollment: (enrollmentId) => api.post(`/drip-campaigns/enrollments/${enrollmentId}/pause`),
  resumeEnrollment: (enrollmentId) => api.post(`/drip-campaigns/enrollments/${enrollmentId}/resume`),
  getAnalytics: (id) => api.get(`/drip-campaigns/${id}/analytics`)
};

// Email Gateway API
export const emailGatewayAPI = {
  getSettings: () => api.get('/email-gateway/settings'),
  updateSettings: (data) => api.put('/email-gateway/settings', data),
  getMappings: () => api.get('/email-gateway/mappings'),
  createMapping: (data) => api.post('/email-gateway/mappings', data),
  deleteMapping: (id) => api.delete(`/email-gateway/mappings/${id}`),
  getLogs: (params) => api.get('/email-gateway/logs', { params }),
  test: (test_email) => api.post('/email-gateway/test', { test_email })
};

// Reports API
export const reportsAPI = {
  getMessageVolume: (params) => api.get('/reports/message-volume', { params }),
  getConversationStats: (params) => api.get('/reports/conversation-stats', { params }),
  getUserPerformance: (params) => api.get('/reports/user-performance', { params }),
  getContactGrowth: (params) => api.get('/reports/contact-growth', { params }),
  getBusiestTimes: (params) => api.get('/reports/busiest-times', { params }),
  getTopContacts: (params) => api.get('/reports/top-contacts', { params }),
  getMessageStatus: (params) => api.get('/reports/message-status', { params }),
  getSummary: (params) => api.get('/reports/summary', { params }),
  getPeakHours: (params) => api.get('/reports/peak-hours', { params }),
  getOptOutStats: (params) => api.get('/reports/opt-out-stats', { params }),
  exportReport: (data) => api.post('/reports/export', data, { responseType: 'blob' }),
  getSoleProprietorCSV: (params) => api.get('/reports/sole-proprietor-csv', { params, responseType: 'blob' }),
  getUserStats: ({ days, startDate, endDate, search = '', page = 1, limit = 10 } = {}) => {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    } else {
      params.append('days', days || 30);
    }
    params.append('search', search);
    params.append('page', page);
    params.append('limit', limit);
    return api.get(`/reports/user-stats?${params.toString()}`);
  },
  getConversationsForExport: ({ days, startDate, endDate, userId, page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    } else {
      params.append('days', days || 30);
    }
    if (userId) params.append('userId', userId);
    params.append('page', page);
    params.append('limit', limit);
    return api.get(`/reports/conversations-export?${params.toString()}`);
  },
  exportConversations: (conversationIds, format = 'csv') =>
    api.post('/reports/export-conversations', { conversationIds, format }, { responseType: 'blob' })
};

// Salesforce Integration API
export const salesforceAPI = {
  saveCredentials: (clientId, clientSecret) => api.post('/salesforce/credentials', { clientId, clientSecret }),
  getCredentialsStatus: () => api.get('/salesforce/credentials/status'),
  getAuthUrl: (isSandbox) => api.get('/salesforce/auth-url', { params: { isSandbox } }),
  callback: (code, isSandbox) => api.post('/salesforce/callback', { code, isSandbox }),
  getStatus: () => api.get('/salesforce/status'),
  disconnect: () => api.post('/salesforce/disconnect'),
  getFieldMappings: () => api.get('/salesforce/field-mappings'),
  saveFieldMapping: (data) => api.post('/salesforce/field-mappings', data),
  deleteFieldMapping: (id) => api.delete(`/salesforce/field-mappings/${id}`),
  getSettings: () => api.get('/salesforce/settings'),
  updateSettings: (settings) => api.put('/salesforce/settings', { settings }),
  syncContact: (contactId) => api.post(`/salesforce/sync/contact/${contactId}`),
  syncAllContacts: () => api.post('/salesforce/sync/all-contacts'),
  getSyncLogs: (limit) => api.get('/salesforce/sync-logs', { params: { limit } }),
  describeObject: (objectType) => api.get(`/salesforce/objects/${objectType}/describe`)
};

// Microsoft Teams Integration API
export const teamsAPI = {
  saveCredentials: (clientId, clientSecret, tenantId, clientSecretExpiresAt) => api.post('/teams/credentials', { clientId, clientSecret, tenantId, clientSecretExpiresAt }),
  getCredentialsStatus: () => api.get('/teams/credentials/status'),
  detectSecretExpiration: () => api.post('/teams/detect-secret-expiration'),
  saveReminderSettings: (enabled, emails) => api.put('/teams/reminder-settings', { enabled, emails }),
  getAuthUrl: () => api.get('/teams/auth-url'),
  callback: (code) => api.post('/teams/callback', { code }),
  getStatus: () => api.get('/teams/status'),
  disconnect: () => api.post('/teams/disconnect'),
  getTeams: () => api.get('/teams/teams'),
  getTeamChannels: (teamId) => api.get(`/teams/teams/${teamId}/channels`),
  getChats: () => api.get('/teams/chats'),
  getChannelMappings: () => api.get('/teams/channel-mappings'),
  createChannelMapping: (data) => api.post('/teams/channel-mappings', data),
  updateChannelMapping: (id, data) => api.patch(`/teams/channel-mappings/${id}`, data),
  deleteChannelMapping: (id) => api.delete(`/teams/channel-mappings/${id}`),
  getSettings: () => api.get('/teams/settings'),
  updateSettings: (settings) => api.put('/teams/settings', { settings }),
  testNotification: (payload) => api.post('/teams/test-notification', payload)
};

// ShipStation Integration API
export const shipstationAPI = {
  getStatus: () => api.get('/shipstation/status'),
  saveApiKey: (apiKey) => api.post('/shipstation/credentials', { apiKey }),
  verify: () => api.post('/shipstation/verify'),
  disconnect: () => api.post('/shipstation/disconnect'),
  listNotifications: () => api.get('/shipstation/notifications'),
  createNotification: (data) => api.post('/shipstation/notifications', data),
  updateNotification: (id, data) => api.patch(`/shipstation/notifications/${id}`, data),
  deleteNotification: (id) => api.delete(`/shipstation/notifications/${id}`),
  testNotification: (id) => api.post(`/shipstation/notifications/${id}/test`),
  previewNotification: (draft) => api.post('/shipstation/notifications/preview', draft),
};

// Business Hours API
export const businessHoursAPI = {
  get: () => api.get('/business-hours'),
  update: (data) => api.put('/business-hours', data),
  getStatus: () => api.get('/business-hours/status')
};

// Scheduled Reports API
export const scheduledReportsAPI = {
  getAll: () => api.get('/scheduled-reports'),
  create: (data) => api.post('/scheduled-reports', data),
  update: (id, data) => api.put(`/scheduled-reports/${id}`, data),
  delete: (id) => api.delete(`/scheduled-reports/${id}`),
  sendTest: (id) => api.post(`/scheduled-reports/${id}/test`)
};

// Clio Integration API
export const clioAPI = {
  saveCredentials: (clientId, clientSecret) => api.post('/clio/credentials', { clientId, clientSecret }),
  getCredentialsStatus: () => api.get('/clio/credentials/status'),
  getAuthUrl: () => api.get('/clio/auth-url'),
  callback: (code) => api.post('/clio/callback', { code }),
  getStatus: () => api.get('/clio/status'),
  disconnect: () => api.post('/clio/disconnect'),
  getContacts: (params) => api.get('/clio/contacts', { params }),
  getMatters: (params) => api.get('/clio/matters', { params }),
  getMattersByContact: (contactId) => api.get(`/clio/matters/by-contact/${contactId}`),
  syncContact: (contactId) => api.post(`/clio/sync/contact/${contactId}`),
  logMessage: (messageId, matterId) => api.post(`/clio/log-message/${messageId}`, { matterId }),
  getSettings: () => api.get('/clio/settings'),
  updateSettings: (settings) => api.put('/clio/settings', { settings }),
  getContactMappings: () => api.get('/clio/contact-mappings'),
  linkMatter: (data) => api.post('/clio/link-matter', data),
  unlinkMatter: (conversationId) => api.delete(`/clio/unlink-matter/${conversationId}`),
  getConversationMatter: (conversationId) => api.get(`/clio/conversation-matter/${conversationId}`),
  getSyncLogs: (limit) => api.get('/clio/sync-logs', { params: { limit } })
};

// Meevo Integration API (Salon/Spa Management)
export const meevoAPI = {
  // Credentials
  saveCredentials: (apiKey, locationId) => api.post('/meevo/credentials', { apiKey, locationId }),
  getCredentialsStatus: () => api.get('/meevo/credentials/status'),
  testConnection: () => api.post('/meevo/test'),
  disconnect: () => api.post('/meevo/disconnect'),

  // Contact Sync
  syncContactsFromMeevo: () => api.post('/meevo/sync/contacts'),
  syncContactsToMeevo: () => api.post('/meevo/sync/contacts/to-meevo'),
  getContactMappings: (params) => api.get('/meevo/contacts/mappings', { params }),

  // Appointments
  syncAppointments: () => api.post('/meevo/sync/appointments'),
  getAppointments: (params) => api.get('/meevo/appointments', { params }),

  // Reminder Settings
  getReminderSettings: () => api.get('/meevo/reminders/settings'),
  saveReminderSettings: (settings) => api.post('/meevo/reminders/settings', settings),

  // Sync History
  getSyncHistory: (limit = 20) => api.get('/meevo/sync/history', { params: { limit } }),

  // Webhooks
  getWebhookEvents: (params) => api.get('/meevo/webhook/events', { params })
};

// Push Notifications API
export const pushAPI = {
  register: (deviceToken, platform, deviceInfo) => api.post('/push/register', { deviceToken, platform, ...deviceInfo }),
  unregister: (deviceToken) => api.post('/push/unregister', { deviceToken }),
  getDevices: () => api.get('/push/devices'),
  test: () => api.post('/push/test'),
  getStatus: () => api.get('/push/status'),
  configure: (config) => api.post('/push/configure', config),
  configureFcm: (config) => api.post('/push/configure-fcm', config),
  getRegistrations: () => api.get('/push/registrations'),
  deactivateRegistration: (id) => api.post(`/push/registrations/${id}/deactivate`),
  getDeliveryLog: (limit) => api.get('/push/delivery-log', { params: { limit } })
};

// AI Analytics API
export const aiAnalyticsAPI = {
  getConversationAnalytics: (conversationId, refresh = false) =>
    api.get(`/ai-analytics/conversation/${conversationId}${refresh ? '?refresh=true' : ''}`),
  analyzeConversation: (conversationId) =>
    api.post(`/ai-analytics/conversation/${conversationId}/analyze`),
  bulkAnalyze: (limit = 50) =>
    api.post('/ai-analytics/bulk-analyze', { limit }),
  getSummary: (days = 30) =>
    api.get(`/ai-analytics/summary?days=${days}`),
  getConversationsAnalytics: (conversationIds) =>
    api.get(`/ai-analytics/conversations?ids=${conversationIds.join(',')}`),
  getTrends: (days = 30, groupBy = 'day') =>
    api.get(`/ai-analytics/trends?days=${days}&groupBy=${groupBy}`),
  getKeywords: (days = 30, limit = 20) =>
    api.get(`/ai-analytics/keywords?days=${days}&limit=${limit}`),
  getUsage: (days = 30) =>
    api.get(`/ai-analytics/usage?days=${days}`),
  getReport: ({ days, startDate, endDate } = {}) => {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    } else {
      params.append('days', days || 30);
    }
    return api.get(`/ai-analytics/report?${params.toString()}`);
  },
  getUserStats: ({ days, startDate, endDate, search = '', page = 1, limit = 10 } = {}) => {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    } else {
      params.append('days', days || 30);
    }
    params.append('search', search);
    params.append('page', page);
    params.append('limit', limit);
    return api.get(`/ai-analytics/user-stats?${params.toString()}`);
  },
  getTokenUsage: (days = 30) =>
    api.get(`/ai-analytics/token-usage?days=${days}`)
};


// ── Supabase PostgREST overrides (feature-flagged) ──
// When REACT_APP_USE_SUPABASE_DATA=true and Supabase auth is active,
// read-heavy CRUD routes go directly to PostgREST instead of Express.
if (isSupabaseDataEnabled()) {
  // Blocked Senders: full CRUD via PostgREST
  Object.assign(blockedSendersAPI, supabaseBlockedSendersAPI);

  // Tags: full CRUD via PostgREST
  tagsAPI.getAll = supabaseTagsAPI.getAll;
  tagsAPI.getById = supabaseTagsAPI.getById;
  tagsAPI.getConversationTags = supabaseTagsAPI.getConversationTags;
  tagsAPI.create = supabaseTagsAPI.create;
  tagsAPI.update = supabaseTagsAPI.update;
  tagsAPI.delete = supabaseTagsAPI.delete;

  // Templates: full CRUD via PostgREST
  templatesAPI.getAll = supabaseTemplatesAPI.getAll;
  templatesAPI.getById = supabaseTemplatesAPI.getById;
  templatesAPI.getCategories = supabaseTemplatesAPI.getCategories;
  templatesAPI.create = supabaseTemplatesAPI.create;
  templatesAPI.update = supabaseTemplatesAPI.update;
  templatesAPI.delete = supabaseTemplatesAPI.delete;

  // Lists: all operations stay in Express (RLS blocks authenticated user access to opt_out_list/allow_list)

  // Contacts: reads/writes via PostgREST, but getAll stays in Express (needs opt_out_list JOIN + user_id filtering)
  // contactsAPI.getAll stays in Express
  contactsAPI.getById = supabaseContactsAPI.getById;
  contactsAPI.create = supabaseContactsAPI.create;
  contactsAPI.update = supabaseContactsAPI.update;
  contactsAPI.delete = supabaseContactsAPI.delete;
}

export default api;
